import * as React from 'react';

import { Link as GatsbyLink, GatsbyLinkProps } from 'gatsby';
import styled, { css } from 'styled-components';

type LinkVariant = 'green' | 'unstyled';

type LinkProps = Omit<GatsbyLinkProps<undefined>, 'to' | 'state' | 'ref'> & {
  /* Internal urls should match: `/some/page/` – external can be anything */
  url: string;
  /* Open link in a new browser tab */
  openInNewTab?: boolean;
  /* Use internal routing */
  external?: boolean;
  /* Link style variant - defaults to 'green' */
  variant?: LinkVariant;
};

/**
 * NOTE: for some reason, calling `styled(Link)` can break dev build,
 * though without affecting prod build...
 * Recommend using <StyledItem as={Link} /> rather than StyledItem = styled(Link)
 * and using `&& {}` inside styles.
 * See: HeaderLogo as an example.
 */
export function Link({
  url,
  activeClassName,
  activeStyle,
  partiallyActive,
  replace,
  openInNewTab,
  external,
  variant = 'green',
  children,
  ...props
}: LinkProps) {
  // Assumes that any internal link (intended for Gatsby)
  // will start with exactly one slash, and that anything else is external.
  const internal = external === true ? false : /^\/(?!\/)/.test(url);

  if (internal) {
    return (
      <InternalLink
        to={url}
        activeClassName={activeClassName}
        activeStyle={activeStyle}
        partiallyActive={partiallyActive}
        replace={replace}
        $variant={variant}
        {...props}
      >
        {children}
      </InternalLink>
    );
  }
  return (
    <ExternalLink
      href={url}
      {...(openInNewTab && { target: '_blank' })}
      $variant={variant}
      {...props}
    >
      {children}
    </ExternalLink>
  );
}

const linkStyles: Record<LinkVariant, ReturnType<typeof css>> = {
  green: css`
    color: var(--stile-green-2);
    font-weight: 400;
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: var(--stile-green-1);
    }
  `,
  unstyled: css`
    /* No additional styling - inherits from parent */
  `,
};

interface StyledLinkProps {
  $variant?: LinkVariant;
}

const InternalLink = styled(GatsbyLink)<StyledLinkProps>`
  ${({ $variant }) => $variant && linkStyles[$variant]}
`;

const ExternalLink = styled.a<StyledLinkProps>`
  ${({ $variant }) => $variant && linkStyles[$variant]}
`;
